import React, { useRef, useEffect, useCallback, memo } from 'react';

import css from './FeedInlineAttachments.module.css';
import { ReactHlsPlayer } from '../../../components';

const FeedInlineAttachments = props => {
  const {
    inline_attachments = [],
    attachments = [],
    isRequestSpace = false,
    onHandleItemClick = () => {},
    onMediaClick = () => {},
  } = props;

  const playerRefs = useRef([]); // Store video container refs
  const gifPlayerRefs = useRef([]); // Store gif-like video refs

  // IntersectionObserver to pause videos when out of view
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting) {
            if (!video.paused) {
              video.play().catch(() => {});
            }
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    playerRefs.current.forEach(player => {
      if (player && player.querySelector('video')) {
        observer.observe(player.querySelector('video'));
      }
    });

    return () => {
      playerRefs.current.forEach(player => {
        if (player && player.querySelector('video')) {
          observer.unobserve(player.querySelector('video'));
        }
      });
    };
  }, []);

  // Ensure gifs loop like gifs
  const handleVideoEnd = useCallback(videoEl => {
    if (videoEl) {
      videoEl.currentTime = 0;
      videoEl.play().catch(() => {});
    }
  }, []);

  // Safari autoplay fix: force inline + mute
  useEffect(() => {
    gifPlayerRefs.current.forEach(player => {
      if (player) {
        const video = player.querySelector('video');
        if (video) {
          video.setAttribute('playsinline', 'true');
          video.setAttribute('muted', 'true');
        }
      }
    });
  }, []);

  const renderAttachment = useCallback(
    (attachment, index, isInline = false) => {
      if (!attachment) return null;
      // Helper: detect image by content_type, presence of image_variants, or file extension
      const isImageLike =
        attachment.content_type?.startsWith('image/') ||
        /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(attachment.url || '');
      // Helper: detect video by content_type, filename or URL extension, or hls variant
      const urlOrName = attachment.url || attachment.filename || '';
      const isVideoLike =
        attachment.content_type?.startsWith('video/') ||
        /\.(mp4|mov|webm|m4v)$/i.test(urlOrName) ||
        !!attachment.video_variants?.hls;
      const transcodingStatus = attachment.media_transcoding?.status?.toLowerCase();
      const isTranscoding =
        (typeof attachment.url === 'string' && attachment.url.startsWith('blob:')) ||
        Boolean(
          transcodingStatus &&
            !['succeeded', 'failed', 'complete', 'completed'].includes(transcodingStatus)
        );

      // Check if it's a GIF (exclude from lightbox — handled separately below)

      // Images
      if (attachment.content_type === 'image/gif') {
        return (
          <img
            key={index}
            src={attachment.url}
            className={isInline ? css.Smallimage : css.image}
            onClick={() => onHandleItemClick(attachment)}
          />
        );
      } else if (isImageLike) {
        const imageSrc =
          attachment.image_variants?.large ||
          attachment.image_variants?.small ||
          attachment.image_variants?.thumbnail ||
          attachment.url ||
          attachment.thq_media_url;

        return (
          <img
            key={index}
            src={imageSrc}
            className={isInline ? css.Smallimage : css.image}
            onClick={e => {
              e.stopPropagation();
              onMediaClick(attachment, index);
            }}
            style={{ cursor: 'pointer' }}
          />
        );
      }

      // Special giphy.gif (loop like a GIF)
      if (attachment.filename === 'giphy.gif') {
        return (
          <div
            className={css.videoContainer}
            key={index}
            ref={el => (gifPlayerRefs.current[index] = el)}
          >
            {attachment.video_variants?.hls ? (
              <ReactHlsPlayer
                src={attachment.video_variants.hls}
                autoPlay={true}
                playsInline
                width="100%"
                height="auto"
                muted={true}
                poster={attachment.image_variants?.thumbnail}
                onEnded={e => handleVideoEnd(e.target)}
              />
            ) : (
              <div>Media attachment upload in progress...</div>
            )}
          </div>
        );
      }

      // Regular videos
      if (isVideoLike) {
        return (
          <div
            className={css.videoContainer}
            key={index}
            ref={el => (playerRefs.current[index] = el)}
          >
            {/* Expand button top-left — opens in lightbox, does not interfere with playback */}
            {/* <button
            type="button"
            className={css.expandBtn}
            onClick={e => {
              e.stopPropagation();
              onMediaClick(attachment, index);
            }}
            aria-label="Open video fullscreen"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 6V1H6" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M15 6V1H10" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M1 10V15H6" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M15 10V15H10" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button> */}

            {attachment.video_variants?.hls ? (
              <ReactHlsPlayer
                src={attachment.video_variants.hls}
                autoPlay={false}
                playsInline
                controls={!isRequestSpace}
                width="100%"
                height="auto"
                muted={true}
                poster={attachment.image_variants?.thumbnail}
              />
            ) : attachment.video_variants?.mp4 ? (
              <video
                key={`video-${index}`}
                src={attachment.video_variants.mp4}
                controls={!isRequestSpace}
                playsInline
                muted
                width="100%"
                height="auto"
                poster={attachment.image_variants?.thumbnail}
              />
            ) : attachment.content_type?.startsWith('video/') && attachment.url ? (
              <video
                key={`video-${index}`}
                src={attachment.url}
                controls={!isRequestSpace}
                playsInline
                muted
                width="100%"
                height="auto"
                poster={attachment.image_variants?.thumbnail}
              />
            ) : urlOrName && /\.(mp4|mov|webm|m4v)$/i.test(urlOrName) ? (
              <video
                key={`video-${index}`}
                src={attachment.url}
                controls={!isRequestSpace}
                playsInline
                muted
                width="100%"
                height="auto"
                poster={attachment.image_variants?.thumbnail}
              />
            ) : (
              <div className={css.transcodingPlaceholder}>
                {isTranscoding ? 'Video is processing…' : 'Video preview unavailable'}
              </div>
            )}
            {isTranscoding && (attachment.url || attachment.video_variants?.mp4) ? (
              <div className={css.transcodingIndicator}>Processing video…</div>
            ) : null}
          </div>
        );
      }

      return null;
    },
    [isRequestSpace, onHandleItemClick, onMediaClick, handleVideoEnd]
  );

  return (
    <div className={css.galleryUpload}>
      {/* Normal attachments */}
      {attachments
        ?.filter(attachment => attachment?.type !== 'embed')
        ?.map((attachment, index) => renderAttachment(attachment, index))}

      {/* Inline attachments */}
      {inline_attachments
        ?.filter(attachment => attachment?.type !== 'embed')
        ?.map((attachment, index) => renderAttachment(attachment, index, true))}
    </div>
  );
};

// shallow comparator to avoid re-renders when attachments haven't meaningfully changed
function attachmentsShallowEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const ai = a[i] || {};
    const bi = b[i] || {};
    if (ai.id && bi.id) {
      if (ai.id !== bi.id) return false;
    } else if (ai.url && bi.url) {
      if (ai.url !== bi.url) return false;
    } else if (ai !== bi) {
      return false;
    }
  }
  return true;
}

function arePropsEqual(prevProps, nextProps) {
  if (prevProps.isRequestSpace !== nextProps.isRequestSpace) return false;
  if (prevProps.onHandleItemClick !== nextProps.onHandleItemClick) return false;
  if (prevProps.onMediaClick !== nextProps.onMediaClick) return false;
  if (!attachmentsShallowEqual(prevProps.attachments, nextProps.attachments)) return false;
  if (!attachmentsShallowEqual(prevProps.inline_attachments, nextProps.inline_attachments))
    return false;
  return true;
}

export default memo(FeedInlineAttachments, arePropsEqual);
